/**
 * ForensicsReportCard — Inline rendering of a `json-forensics` block.
 *
 * Rendered by MessageContent when it detects a fenced code block with
 * language `json-forensics`. The block body is the JSON output of the
 * EveCare tool `analyze_container_history` (which delegates to
 * EveMasterContainer in user_inquiry mode).
 *
 * Layout:
 * - Header: title + confidence badge + expand toggle
 * - Domanda (user question)
 * - Sintesi (analysis_summary)
 * - Findings list with severity badges
 * - Fonti consultate (chips)
 * - Reasoning (collapsed by default, full text)
 */

import { useState } from "react";
import {
    SearchLg,
    ChevronDown,
    ChevronRight,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    InfoCircle,
} from "@untitledui/icons";

export interface ForensicsFinding {
    title?: string;
    severity?: "info" | "warning" | "error" | string;
    field?: string;
    detail?: string;
    evidence?: string[];
}

export interface ForensicsReport {
    success?: boolean;
    error?: string;
    report_type?: string;
    container_id?: string;
    container_number?: string | null;
    user_question?: string;
    reasoning?: string;
    analysis_summary?: string;
    findings?: ForensicsFinding[];
    sources_consulted?: string[];
    confidence?: number;
    related_container_ids?: string[];
    parse_warning?: string;
}

interface Props {
    report: ForensicsReport;
}

const SEVERITY_CONFIG: Record<
    string,
    {
        icon: React.ComponentType<{ className?: string }>;
        color: string;
        label: string;
    }
> = {
    error: {
        icon: AlertCircle,
        color: "text-red-700 bg-red-100 dark:bg-red-950/30 dark:text-red-300",
        label: "Errore",
    },
    warning: {
        icon: AlertTriangle,
        color: "text-yellow-700 bg-yellow-100 dark:bg-yellow-950/30 dark:text-yellow-300",
        label: "Attenzione",
    },
    info: {
        icon: InfoCircle,
        color: "text-blue-700 bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300",
        label: "Info",
    },
};

const SOURCE_LABEL: Record<string, string> = {
    jsoncargo: "Tracking carrier",
    search_containers: "Database container",
    get_email_thread: "Thread email",
    get_company_context: "Knowledge base",
    get_container_documents: "Documenti",
    check_container_tracking: "Tracking JSONCargo",
    manual_user: "Modifica manuale",
    document_extraction: "Estrazione documento",
    email_body: "Corpo email",
    company_kb: "Knowledge base",
};

function formatSourceLabel(raw: string): string {
    if (SOURCE_LABEL[raw]) return SOURCE_LABEL[raw];
    // Handle "email:<uuid>" / "document:<uuid>" patterns
    if (raw.startsWith("email:")) return `Email ${raw.slice(6, 14)}...`;
    if (raw.startsWith("document:")) return `Documento ${raw.slice(9, 17)}...`;
    return raw;
}

function severityOf(finding: ForensicsFinding) {
    const key = (finding.severity ?? "info").toLowerCase();
    return SEVERITY_CONFIG[key] ?? SEVERITY_CONFIG.info;
}

export const ForensicsReportCard = ({ report }: Props) => {
    const [expanded, setExpanded] = useState(true);
    const [reasoningOpen, setReasoningOpen] = useState(false);

    if (!report.success) {
        return (
            <div className="my-3 rounded-lg border border-red-300/50 bg-red-50/50 p-3 text-sm text-red-700 dark:bg-red-950/20 dark:text-red-300">
                <div className="flex items-center gap-2 font-medium">
                    <AlertCircle className="size-4" />
                    Analisi forensics fallita
                </div>
                <div className="mt-1 text-xs">{report.error ?? "Errore sconosciuto"}</div>
            </div>
        );
    }

    const confidencePct =
        typeof report.confidence === "number" ? Math.round(report.confidence * 100) : null;

    const findings = report.findings ?? [];
    const sources = report.sources_consulted ?? [];

    return (
        <div className="my-3 rounded-xl border border-secondary bg-secondary/20 overflow-hidden">
            {/* Header */}
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/40 transition-colors"
            >
                {expanded ? (
                    <ChevronDown className="size-4 shrink-0 text-tertiary" />
                ) : (
                    <ChevronRight className="size-4 shrink-0 text-tertiary" />
                )}
                <div className="flex items-center gap-2 shrink-0">
                    <SearchLg className="size-5 text-brand" />
                    <span className="font-semibold text-primary">Analisi approfondita</span>
                </div>
                {report.container_number && (
                    <span className="text-xs text-tertiary font-mono truncate">
                        {report.container_number}
                    </span>
                )}
                <div className="flex-1" />
                {findings.length > 0 && (
                    <span className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-secondary/70 text-primary">
                        {findings.length} {findings.length === 1 ? "finding" : "findings"}
                    </span>
                )}
                {confidencePct !== null && (
                    <span
                        className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                            confidencePct >= 75
                                ? "bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300"
                                : confidencePct >= 50
                                  ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300"
                                  : "bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300"
                        }`}
                    >
                        conf. {confidencePct}%
                    </span>
                )}
            </button>

            {expanded && (
                <div className="border-t border-secondary px-4 py-3 space-y-4">
                    {/* Domanda */}
                    {report.user_question && (
                        <div>
                            <div className="text-xs text-tertiary uppercase tracking-wide mb-1">
                                Domanda
                            </div>
                            <div className="text-sm text-primary italic border-l-2 border-brand pl-3">
                                "{report.user_question}"
                            </div>
                        </div>
                    )}

                    {/* Sintesi */}
                    {report.analysis_summary && (
                        <div>
                            <div className="text-xs text-tertiary uppercase tracking-wide mb-1">
                                Sintesi
                            </div>
                            <div className="text-sm text-primary whitespace-pre-wrap">
                                {report.analysis_summary}
                            </div>
                        </div>
                    )}

                    {/* Findings */}
                    {findings.length > 0 && (
                        <div>
                            <div className="text-xs text-tertiary uppercase tracking-wide mb-2">
                                Findings
                            </div>
                            <div className="space-y-2">
                                {findings.map((finding, idx) => {
                                    const cfg = severityOf(finding);
                                    const Icon = cfg.icon;
                                    return (
                                        <div
                                            key={idx}
                                            className="rounded-lg border border-secondary bg-primary/5 p-3"
                                        >
                                            <div className="flex items-start gap-2">
                                                <div
                                                    className={`shrink-0 rounded-md p-1 ${cfg.color}`}
                                                >
                                                    <Icon className="size-4" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-medium text-primary text-sm">
                                                            {finding.title ?? cfg.label}
                                                        </span>
                                                        {finding.field && (
                                                            <span className="text-xs text-tertiary font-mono">
                                                                {finding.field}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {finding.detail && (
                                                        <div className="text-sm text-secondary_hover mt-1 whitespace-pre-wrap">
                                                            {finding.detail}
                                                        </div>
                                                    )}
                                                    {finding.evidence && finding.evidence.length > 0 && (
                                                        <div className="mt-2 flex flex-wrap gap-1">
                                                            {finding.evidence.map((ev, i) => (
                                                                <span
                                                                    key={i}
                                                                    className="text-xs px-2 py-0.5 rounded bg-secondary text-primary font-mono"
                                                                >
                                                                    {ev}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {findings.length === 0 && (
                        <div className="flex items-center gap-2 text-sm text-tertiary">
                            <CheckCircle className="size-4 text-green-500" />
                            Nessuna anomalia rilevata.
                        </div>
                    )}

                    {/* Fonti consultate */}
                    {sources.length > 0 && (
                        <div>
                            <div className="text-xs text-tertiary uppercase tracking-wide mb-2">
                                Fonti consultate
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {sources.map((src, idx) => (
                                    <span
                                        key={idx}
                                        className="text-xs px-2 py-1 rounded-full bg-secondary text-primary"
                                    >
                                        {formatSourceLabel(src)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Reasoning collapsible */}
                    {report.reasoning && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setReasoningOpen((v) => !v)}
                                className="flex items-center gap-2 text-xs text-tertiary uppercase tracking-wide hover:text-primary"
                            >
                                {reasoningOpen ? (
                                    <ChevronDown className="size-3" />
                                ) : (
                                    <ChevronRight className="size-3" />
                                )}
                                Ragionamento completo
                            </button>
                            {reasoningOpen && (
                                <div className="mt-2 text-xs text-secondary_hover whitespace-pre-wrap font-mono bg-primary/5 p-3 rounded border border-secondary">
                                    {report.reasoning}
                                </div>
                            )}
                        </div>
                    )}

                    {report.parse_warning && (
                        <div className="text-xs text-yellow-600 dark:text-yellow-400">
                            ⚠️ Formato risposta parzialmente interpretato: {report.parse_warning}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
